import type { OrderCalculation } from '../../../types/order-calculation';
import type { TicketOfficeView } from '../../../types/ticket-office';
import { OrderSummaryFooterProps } from '../../components/OrderSummaryFooter';
import { RadioOption } from '../../components/Radio';
import { formatPrice } from '../../number-utils';
import { Api } from '../api';
import { completeSale, onSubmit } from './OrderSummary';
import { updateCalc } from './update-calc';

export const getOrderSummaryFooterProps = (view: TicketOfficeView): OrderSummaryFooterProps => {
    const { state, update } = view;
    const { currencySymbol, lang, calc } = state;

    const formattedTotal = formatPrice(calc.total, currencySymbol);
    const hasPartialPayments = calc.partialPayments.length > 0;
    const toPayTotal = formatPrice(calc.toPay, currencySymbol);

    // Items in the basket and nothing left to pay (free tickets, or serial tickets, gift cards and the like cover
    // it all): one button completes the sale, and of the payment options only Booking stays where it was.
    const offered = state.paymentMethodOptions.filter((option) => !calc.nothingToPay || option.value === 'booking');
    const menu = offered.filter((option) => !option.button);

    return {
        isDropdownExpanded: state.paymentMethodsExpanded,
        isSubmitting: state.isSubmitting,
        onDropdownButtonClick: () => {
            state.paymentMethodsExpanded = !state.paymentMethodsExpanded;
            update();
        },
        onDropdownOutsideClick: () => {
            if (state.paymentMethodsExpanded) {
                state.paymentMethodsExpanded = false;
                update();
            }
        },
        print: {
            label: lang.summary.print,
            checked: state.print,
            onChange: (checked): void => {
                state.print = checked;
                update();
            },
            isDisabled: !state.isPrinterConnected,
            onMouseLeave:
                !state.isPrinterConnected &&
                (() => {
                    state.showPrintTooltip = false;
                    update();
                }),
            onMouseEnter:
                !state.isPrinterConnected &&
                (() => {
                    state.showPrintTooltip = true;
                    update();
                }),
        },
        printTooltip: state.showPrintTooltip && lang.summary.printerNotConnected,
        printReceipt: {
            label: lang.summary.printReceipt,
            checked: state.printReceipt,
            onChange: (checked): void => {
                state.printReceipt = checked;
                update();
            },
            isDisabled: !state.isReceiptPrinterConnected,
            onMouseLeave:
                !state.isReceiptPrinterConnected &&
                (() => {
                    state.showPrintReceiptTooltip = false;
                    update();
                }),
            onMouseEnter:
                !state.isReceiptPrinterConnected &&
                (() => {
                    state.showPrintReceiptTooltip = true;
                    update();
                }),
        },
        printReceiptTooltip: state.showPrintReceiptTooltip && lang.summary.printerNotConnected,
        partialPayments: calc.partialPayments.map(({ method, label, amount }) => ({
            text: label,
            amount: formatPrice(-amount, currencySymbol),
            onRemove: async () => {
                const reqBody = {
                    sessionId: state.sessionId,
                    method,
                };

                const response = await Api.deleteJson(state.urls.removePartialPayment, state.csrfToken, reqBody);

                if (!response.ok) {
                    window.toastr.error('Error'); // TODO
                } else {
                    const resBody = (await response.json()) as {
                        calc: OrderCalculation;
                    };

                    updateCalc(view, resBody.calc);
                }

                update();
            },
        })),
        totalSum: hasPartialPayments && {
            text: lang.summary.total,
            amount: formattedTotal,
        },
        remainingSum: {
            text: hasPartialPayments ? lang.summary.toPay : lang.summary.total,
            amount: hasPartialPayments ? toPayTotal : formattedTotal,
        },
        onSubmit: (paymentMethod: RadioOption) => {
            onSubmit(view, paymentMethod);
        },
        onSubmitAdditional: (paymentMethod) => {
            state.paymentMethodsExpanded = false;
            onSubmit(view, paymentMethod);
        },
        paymentOptions: offered.filter((option) => option.button),
        completeSale: calc.nothingToPay
            ? {
                  label: lang.summary.completeSale,
                  onClick: () => completeSale(view),
              }
            : undefined,
        // No ⋯ menu when every method is a button.
        additionalPaymentOptions: menu.length > 0 ? menu : undefined,
    };
};
